import { report } from "@chatfood/bug-reporter";
import { getQueryParamValue } from "@chatfood/core-utils";
import {
  abortNavigation,
  defineNuxtRouteMiddleware,
  navigateTo,
  useError,
} from "nuxt/app";
import { isOutletRepoError } from "@chatfood/dbe-client/errors";
import { useDirectBackend, usePinia } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";

const abortWithUnexpectedError = (
  message: string = "We could not load the location details. Please try again in a few minutes.",
) => {
  return abortNavigation({
    message,
    statusCode: 500,
  });
};

export default defineNuxtRouteMiddleware(async (to) => {
  if (useError().value) return;

  const routeOutlet = getQueryParamValue(to.params, "outletSlug");

  if (!routeOutlet) {
    return abortWithUnexpectedError(
      "EnsureOutletIsLoadedMiddleware requires a route with outletSlug parameter.",
    );
  }

  const pinia = usePinia();
  const outlet = useOutletStore(pinia);

  if (outlet.isLoaded && outlet.slug === routeOutlet) {
    return;
  }

  const business = useBusinessStore(pinia);
  const basket = useBasketStore(pinia);

  const { outletRepo } = useDirectBackend();

  const orderingModePrimary = basket.orderingMode;
  const orderingModeFallback =
    orderingModePrimary === "delivery" ? "pickup" : "delivery";

  try {
    const [resPrimary, resFallback] = await Promise.all([
      outletRepo.getOutletBySlug({
        businessId: business.id,
        outletSlug: routeOutlet,
        salesChannel: basket.salesChannel,
        orderingMode: orderingModePrimary,
      }),
      outletRepo.getOutletBySlug({
        businessId: business.id,
        outletSlug: routeOutlet,
        salesChannel: basket.salesChannel,
        orderingMode: orderingModeFallback,
      }),
    ]);

    if (!resPrimary) return;

    outlet.reset();

    const hasDelivery =
      orderingModePrimary === "delivery"
        ? resPrimary.settings.isEnabled
        : resFallback?.settings.isEnabled;

    const hasPickup =
      orderingModePrimary === "pickup"
        ? resPrimary.settings.isEnabled
        : resFallback?.settings.isEnabled;

    outlet.patch({
      isLoaded: true,
      id: resPrimary.id,
      name: resPrimary.name,
      slug: resPrimary.slug,
      phone: resPrimary.phone,
      isTaxIncluded: resPrimary.tax.isIncluded,
      hasDelivery,
      hasPickup,
      address: {
        location: resPrimary.address.location,
        city: resPrimary.address.city,
        lat: resPrimary.address.coordinate.lat,
        lng: resPrimary.address.coordinate.lng,
      },
    });

    if (resPrimary.slug !== routeOutlet) {
      return navigateTo(
        {
          name: to.name,
          params: {
            ...to.params,
            outletSlug: resPrimary.slug,
          },
          query: to.query,
          hash: to.hash,
        },
        {
          replace: true,
          redirectCode: 301,
        },
      );
    }
  } catch (error) {
    if (isOutletRepoError(error) && error.isNotFound()) {
      return abortNavigation({
        message: "Outlet not found",
        statusCode: 404,
      });
    }

    report(error, {
      tags: {
        "code.flag": "middleware-outlet",
      },
    });

    return abortWithUnexpectedError();
  }
});
